import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { THEME_LIST } from './storefront/themes/registry'
import { fetchPublicShowcase, type PublicShowcaseEntry } from '../lib/showcase'
import { derivePlanInclusions } from '../lib/planInclusions'

// =============================================================================
// Palstro marketing homepage (palstro.com root, logged-out visitors).
// Faithful port of the approved design file `palstro_marketing_home_v2.html`:
// same sections, layout, copy, brand orange and motion (scroll-reveal + the
// auto-rotating "See it in action" panel). All design CSS is scoped under `.mkt`
// so its generic class names (.card/.btn/.logo/…) never leak into the rest of
// the app. Three sections are wired to live data:
//   • Pricing      — subscription_plans (tiers) + platform_settings (online-store
//                    add-on price). Built to consume the future module-level Plan
//                    Config without a rebuild (same fields/feature list).
//   • Storefronts  — the live THEME_LIST registry (single source of truth).
//   • Testimonials — get_public_showcase() (mig 347); fails soft to the design's
//                    placeholder cards when the reader is absent or empty.
// =============================================================================

const NAIRA = '₦'

// WhatsApp contact — number 09165966000 → international 234 + drop leading 0.
const WHATSAPP_URL = 'https://wa.me/2349165966000?text=Hi%20Palstro%2C%20I%27d%20like%20to%20know%20more'
const WHATSAPP_DISPLAY = '0916 596 6000'

type PlanRow = {
  id: string
  name: string
  monthly_price: number
  annual_price: number | null
  features: unknown
  sort_order: number | null
}

// Row shape from get_public_plan_inclusions() (mig 350).
type RichPlan = {
  id: string
  name: string
  monthly_price: number
  annual_price: number | null
  max_locations: number
  max_products: number
  features_override: unknown
  page_keys: unknown
}

// Normalised tier the pricing cards render, whatever the source.
type PricingTier = {
  id: string
  name: string
  price: number
  inclusions: string[]
}

function fmtNaira(n: number): string {
  return NAIRA + Math.round(n).toLocaleString('en-NG')
}

// Honest billing-period suffix for the add-on price (it's charged per period, not always /mo).
function periodSuffix(months: number): string {
  if (months <= 1) return '/mo'
  if (months === 3) return '/quarter'
  if (months === 12) return '/yr'
  return `/${months} mo`
}

// ── SEE IT IN ACTION — the 4 rotating device screens (static mockups, per design) ──
const SHOWCASE_TABS = ['Dashboard', 'Online store', 'POS', 'Orders'] as const

export default function MarketingHomePage() {
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)

  const goRegister = () => navigate('/register')
  const goLogin = () => navigate('/login')

  // Live pricing tiers.
  const { data: plans = [] } = useQuery({
    queryKey: ['marketing-plans'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('subscription_plans')
        .select('id, name, monthly_price, annual_price, features, sort_order, is_active, is_visible')
        .eq('is_active', true)
        .eq('is_visible', true)
        .order('sort_order', { ascending: true })
      if (error) throw error
      return (data ?? []) as PlanRow[]
    },
  })

  // Page-derived pricing inclusions (single source with the live access gate). Each tier's
  // bullets are generated from its TICKED pages + limits + optional override — via the anon
  // get_public_plan_inclusions() reader (mig 350). Fails soft: if the reader is absent the
  // pricing falls back to the subscription_plans.features path below.
  const { data: richPlans = [] } = useQuery({
    queryKey: ['marketing-plan-inclusions'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<RichPlan[]> => {
      try {
        const { data, error } = await (supabase as any).rpc('get_public_plan_inclusions')
        if (error) return []
        return (Array.isArray(data) ? data : []) as RichPlan[]
      } catch {
        return []
      }
    },
  })

  // Online-store add-on price + billing period + inclusions (platform_settings, anon-readable
  // like the maintenance flag). The add-on is priced per period (e.g. ₦15k / quarter), so we
  // read the period too and label it honestly rather than assuming "/mo". Its inclusions are
  // now editable (online_store_inclusions, JSON array); falls back to the design's copy.
  const { data: onlineStore = { price: 5000, months: 1, inclusions: [] as string[] } } = useQuery({
    queryKey: ['marketing-online-store-price'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('platform_settings')
        .select('key,value')
        .in('key', [
          'online_store_price_ngn',
          'online_store_price_period_months',
          'online_store_period_months',
          'online_store_inclusions',
        ])
      const map: Record<string, string> = {}
      ;(data ?? []).forEach((r: { key: string; value: string }) => { map[r.key] = r.value })
      const price = Number(map['online_store_price_ngn'])
      // Period lives under one of two keys historically (the admin writer set_online_store_pricing
      // uses online_store_period_months; an earlier reader expected online_store_price_period_months).
      // Accept either so the honest /period suffix always reflects the admin's saved value.
      const months = Number(map['online_store_price_period_months'] ?? map['online_store_period_months'])
      let inclusions: string[] = []
      try {
        const parsed = JSON.parse(map['online_store_inclusions'] ?? '[]')
        if (Array.isArray(parsed)) inclusions = parsed.map(String).map(s => s.trim()).filter(Boolean)
      } catch { /* malformed — fall back to design copy */ }
      return {
        price: Number.isFinite(price) && price > 0 ? price : 5000,
        months: Number.isFinite(months) && months > 0 ? months : 1,
        inclusions,
      }
    },
  })

  // Unified pricing tiers: prefer the page-derived reader; else the legacy features path;
  // else the design fallback. Every column reads from ONE place per source, no drift.
  const tiers = useMemo<PricingTier[]>(() => {
    if (richPlans.length) {
      return richPlans.slice(0, 3).map(p => ({
        id: p.id,
        name: p.name,
        price: Number(p.monthly_price) || 0,
        inclusions: derivePlanInclusions(
          Array.isArray(p.page_keys) ? p.page_keys.map(String) : [],
          { maxLocations: Number(p.max_locations), maxProducts: Number(p.max_products) },
          Array.isArray(p.features_override) ? (p.features_override as unknown[]).map(String) : [],
        ),
      }))
    }
    if (plans.length) {
      return plans.slice(0, 3).map((p, i) => ({
        id: p.id,
        name: p.name,
        price: Number(p.monthly_price) || 0,
        inclusions: deriveFeatures(p, i),
      }))
    }
    return FALLBACK_TIERS.map((p, i) => ({
      id: String(i),
      name: p.name,
      price: p.price,
      inclusions: DESIGN_FEATURES[i] ?? [],
    }))
  }, [richPlans, plans])

  // Published showcase entries (fails soft to []). Always revalidate so an admin
  // publish/edit/reorder in the Customer Showcase shows up on the next load or tab
  // focus — never a stale testimonial. The data is fetched client-side at runtime, so
  // a refresh of palstro.com re-runs this against the live get_public_showcase() reader.
  const { data: showcase = [] } = useQuery({
    queryKey: ['marketing-showcase'],
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    queryFn: fetchPublicShowcase,
  })

  // Scroll-reveal.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const els = Array.from(root.querySelectorAll('.reveal'))
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in')
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12 },
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [showcase.length])

  // Auto-rotate the showcase panel (pause on hover).
  useEffect(() => {
    if (paused) return
    const t = setInterval(() => setActive((c) => (c + 1) % SHOWCASE_TABS.length), 4200)
    return () => clearInterval(t)
  }, [paused])

  return (
    <div className="mkt" ref={rootRef}>
      <style>{MKT_CSS}</style>

      {/* NAV */}
      <nav>
        <div className="wrap row">
          <div className="logo"><span className="p">P</span> Palstro</div>
          <div className="navlinks">
            <a href="#why">Why Palstro</a>
            <a href="#see">See it</a>
            <a href="#features">Features</a>
            <a href="#themes">Storefronts</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div className="navcta">
            <a className="login" onClick={goLogin}>Log in</a>
            <a className="btn" onClick={goRegister}>Start free</a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="hero">
        <div className="wrap herogrid">
          <div className="reveal in">
            <div className="eyebrow">Built for Nigerian businesses</div>
            <h1 className="mt">Keep more of <span className="hl">every naira</span> your shop makes.</h1>
            <p className="lead">One app for your counter <b>and</b> your online store &mdash; same stock, same money, one dashboard. Cut the waste, sell more, protect your margins.</p>
            <div className="cta">
              <a className="btn" onClick={goRegister}>Start free</a>
              <a className="btn ghost" href="#why">See how it works</a>
            </div>
            <div className="fine">Free to start &middot; no card needed</div>
            <div className="pricechip">
              <span>&#127829;</span>
              <span><b>Less than a plate of jollof a day.</b> From <span className="amt">{fmtNaira(tiers[0]?.price ?? 7500)}</span>/mo, everything included.</span>
            </div>
          </div>
          <div className="herovis reveal in">
            <div className="frame">
              <div className="bar"><i></i><i></i><i></i><span className="url">app.palstro.com</span></div>
              <div className="dash">
                <div className="dhead"><b>Today</b><span className="pill">&#9650; 18% vs last week</span></div>
                <div className="kpis">
                  <div className="kpi"><b>{NAIRA}248,500</b><span>Sales</span><em>&#9650; 12%</em></div>
                  <div className="kpi"><b>36</b><span>Online orders</span><em>&#9650; 9%</em></div>
                  <div className="kpi"><b>4</b><span>Low stock</span></div>
                </div>
                <div className="drow">
                  <div className="panel"><div className="t">Sales this week</div>
                    <div className="bars">
                      <div className="g" style={{ bottom: '25%' }}></div><div className="g" style={{ bottom: '60%' }}></div>
                      <div className="col"><b style={{ height: '42%' }}></b></div><div className="col"><b style={{ height: '58%' }}></b></div>
                      <div className="col"><b style={{ height: '38%' }}></b></div><div className="col"><b style={{ height: '72%' }}></b></div>
                      <div className="col"><b style={{ height: '55%' }}></b></div><div className="col"><b style={{ height: '88%' }}></b></div>
                      <div className="col"><b style={{ height: '66%' }}></b></div>
                    </div>
                    <div className="lbl"><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span></div>
                  </div>
                  <div className="panel"><div className="t">Where sales come from</div>
                    <div className="donut"></div>
                    <div className="leg">
                      <div><i style={{ background: 'var(--brand)' }}></i>In-store 55%</div>
                      <div><i style={{ background: 'var(--teal)' }}></i>Online 30%</div>
                      <div><i style={{ background: 'var(--slate)' }}></i>WhatsApp 15%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* phone storefront overlay */}
            <div className="phone">
              <div className="store">
                <div className="mq" style={{ fontSize: '6px', letterSpacing: '.1em', padding: '4px' }}>&#10022; Your shop &#10022; Your shop &#10022;</div>
                <div className="sh" style={{ padding: '7px 8px', gap: '6px' }}><div className="lg" style={{ fontSize: '9px' }}>SHOP</div><div className="sr" style={{ fontSize: '7px', padding: '5px 8px' }}>Search&hellip;</div><div className="ac" style={{ fontSize: '9px' }}>&#128722;</div></div>
                <div className="hero2" style={{ height: '56px', padding: '9px' }}><b style={{ fontSize: '12px' }}>Your Shop</b><div className="dots" style={{ bottom: '5px', left: '9px' }}><i className="on"></i><i></i><i></i></div></div>
                <div className="na" style={{ fontSize: '9px', padding: '8px 8px 4px' }}>NEW ARRIVALS</div>
                <div className="pg" style={{ gridTemplateColumns: '1fr 1fr', gap: '6px', padding: '0 8px 8px' }}>
                  <div className="pc"><div className="pill" style={{ fontSize: '6px', padding: '2px 5px' }}>Bags</div><div className="im"></div></div>
                  <div className="pc"><div className="pill" style={{ fontSize: '6px', padding: '2px 5px' }}>Shoes</div><div className="im"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* WHY / PROFIT — three profit levers + an illustrative profit chart */}
      <section id="why" className="profit"><div className="wrap">
        <div className="profitgrid reveal">
          <div className="sec-head">
            <div className="eyebrow">The bottom line</div>
            <h2 className="mt">Three ways Palstro grows your profit.</h2>
            <p className="lead mt">Most tools give you features and leave the maths to you. Palstro works on the number that matters: what you actually keep at the end of the month.</p>
          </div>
          <div className="chartcard" aria-label="Illustrative chart: profit over a year with and without Palstro">
            <div className="top">
              <h3>Your profit over a year</h3>
              <span className="tag">Illustrative</span>
            </div>
            <svg className="chart-svg" viewBox="0 0 560 300" role="img" aria-label="Two lines over twelve months. Without Palstro stays flat near thirty percent; with Palstro rises to about seventy-five percent. The shaded band between them is the extra profit you keep.">
              <g stroke="var(--line)" strokeWidth="1">
                <line x1="48" y1="60" x2="528" y2="60" />
                <line x1="48" y1="122" x2="528" y2="122" />
                <line x1="48" y1="184" x2="528" y2="184" />
                <line x1="48" y1="246" x2="528" y2="246" />
              </g>
              <polygon points="48,184 144,165 240,139 336,110 432,78 528,43 528,174 432,181 336,178 240,184 144,181 48,184" fill="rgba(242,111,33,.13)" />
              <polyline fill="none" stroke="var(--slate)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 7" points="48,184 144,181 240,184 336,178 432,181 528,174" />
              <polyline className="draw" fill="none" stroke="var(--brand)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" points="48,184 144,165 240,139 336,110 432,78 528,43" />
              <circle cx="528" cy="43" r="5" fill="var(--brand)" />
              <text x="502" y="34" textAnchor="end" fontSize="13" fontWeight="700" fill="var(--brand)">With Palstro</text>
              <text x="356" y="200" fontSize="12.5" fill="var(--mut)">Without Palstro</text>
              <text x="262" y="128" fontSize="12.5" fontWeight="700" fill="var(--brand-d)">Extra profit you keep</text>
              <g fontSize="11" fill="var(--mut)">
                <text x="18" y="188">30%</text>
                <text x="18" y="64">75%</text>
              </g>
            </svg>
            <div className="legend">
              <span><i className="swatch" style={{ background: 'var(--brand)' }}></i>With Palstro</span>
              <span><i className="swatch" style={{ background: 'var(--slate)' }}></i>Without</span>
              <span><i className="swatch" style={{ background: 'rgba(242,111,33,.13)', height: '11px', borderRadius: '3px' }}></i>Profit you keep</span>
            </div>
          </div>
        </div>
        <div className="levers reveal">
          <div className="lever">
            <div className="ic"><svg viewBox="0 0 24 24"><path d="M3 17l6-6 4 4 8-8" /><path d="M21 7v5h-5" /></svg></div>
            <h3>Reduce cost</h3>
            <p className="promise">Stop the quiet leaks.</p>
            <p className="how">See where money slips away &mdash; expired stock, shrinkage, overordering &mdash; and cut it. One system replaces the notebooks, spreadsheets and guesswork you pay for in lost stock.</p>
          </div>
          <div className="lever">
            <div className="ic"><svg viewBox="0 0 24 24"><path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-7" /><path d="M22 20H2" /></svg></div>
            <h3>Increase revenue</h3>
            <p className="promise">Sell more, and sell after hours.</p>
            <p className="how">Open a storefront customers buy from day and night, take orders straight to WhatsApp, and never run out of your bestsellers. Every sale is captured &mdash; none walk out untracked.</p>
          </div>
          <div className="lever">
            <div className="ic"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg></div>
            <h3>Manage margins</h3>
            <p className="promise">Price to profit, not to guess.</p>
            <p className="how">Know your true margin on every item, with cost, VAT and fees already worked in. Price with confidence, spot the products quietly losing you money, and protect the ones that pay.</p>
          </div>
        </div>
      </div></section>

      {/* SEE IT IN ACTION */}
      <section id="see"><div className="wrap">
        <div
          className="showcase reveal"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="center">
            <div className="eyebrow" style={{ color: '#ffb37a' }}>See it in action</div>
            <h2 className="mt">Your whole business, on one screen.</h2>
            <p className="lead mt">Dashboard, online store, POS, orders &mdash; flip through it.</p>
          </div>
          <div className="tabs">
            {SHOWCASE_TABS.map((label, i) => (
              <button key={label} className={i === active ? 'on' : ''} onClick={() => { setActive(i) }}>{label}</button>
            ))}
          </div>
          <div className="screens">
            {/* 0 dashboard */}
            <div className={'screen' + (active === 0 ? ' on' : '')}>
              <div className="frame"><div className="bar"><i></i><i></i><i></i><span className="url">app.palstro.com/dashboard</span></div>
                <div className="dash" style={{ padding: '18px' }}>
                  <div className="dhead"><b style={{ fontSize: '15px' }}>Business overview</b><span className="pill">This month &#9650; 22%</span></div>
                  <div className="kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
                    <div className="kpi"><b>{NAIRA}4.2M</b><span>Revenue</span><em>&#9650; 22%</em></div>
                    <div className="kpi"><b>1,284</b><span>Orders</span><em>&#9650; 14%</em></div>
                    <div className="kpi"><b>{NAIRA}1.1M</b><span>Profit</span><em>&#9650; 8%</em></div>
                    <div className="kpi"><b>7</b><span>Needs attention</span></div>
                  </div>
                  <div className="drow">
                    <div className="panel"><div className="t">Revenue trend</div>
                      <div className="bars" style={{ height: '110px' }}>
                        <div className="g" style={{ bottom: '25%' }}></div><div className="g" style={{ bottom: '50%' }}></div><div className="g" style={{ bottom: '75%' }}></div>
                        <div className="col"><b style={{ height: '40%' }}></b></div><div className="col"><b style={{ height: '52%' }}></b></div><div className="col"><b style={{ height: '46%' }}></b></div>
                        <div className="col"><b style={{ height: '64%' }}></b></div><div className="col"><b style={{ height: '58%' }}></b></div><div className="col"><b style={{ height: '78%' }}></b></div>
                        <div className="col"><b style={{ height: '70%' }}></b></div><div className="col"><b style={{ height: '92%' }}></b></div>
                      </div>
                      <div className="lbl"><span>Jan</span><span>Feb</span><span>Mar</span><span>Apr</span><span>May</span><span>Jun</span><span>Jul</span><span>Aug</span></div>
                    </div>
                    <div className="panel"><div className="t">Sales channels</div><div className="donut" style={{ width: '110px', height: '110px', WebkitMaskImage: 'radial-gradient(circle 30px at center,transparent 98%,#000 100%)', maskImage: 'radial-gradient(circle 30px at center,transparent 98%,#000 100%)' }}></div>
                      <div className="leg"><div><i style={{ background: 'var(--brand)' }}></i>In-store</div><div><i style={{ background: 'var(--teal)' }}></i>Online</div><div><i style={{ background: 'var(--slate)' }}></i>WhatsApp</div></div>
                    </div>
                  </div>
                </div></div>
              <div className="cap">One dashboard for in-store and online, with the numbers that matter.</div>
            </div>
            {/* 1 store */}
            <div className={'screen' + (active === 1 ? ' on' : '')}>
              <div className="frame"><div className="bar"><i></i><i></i><i></i><span className="url">yourshop.com</span></div>
                <div className="store">
                  <div className="mq">&#10022; &nbsp; Your shop, your words &nbsp; &#10022; &nbsp; Your shop, your words &nbsp; &#10022; &nbsp; Your shop, your words &nbsp; &#10022;</div>
                  <div className="sh"><div className="lg">YOUR SHOP</div><div className="sr">Search products&hellip;</div><div className="ac">Log in</div><div className="cr">Create account</div><div className="ac">&#128722;</div></div>
                  <div className="hero2"><b>Your Shop</b><small>your tagline here</small><div className="dots"><i className="on"></i><i></i><i></i><i></i></div></div>
                  <div className="na">NEW ARRIVALS</div>
                  <div className="pg">
                    <div className="pc"><div className="pill">Bags</div><div className="im"></div><div className="in"><div className="nm">Leather Tote</div><div className="pr">{NAIRA}42,000</div></div></div>
                    <div className="pc"><div className="pill">Shoes</div><div className="im"></div><div className="in"><div className="nm">Sneakers</div><div className="pr">{NAIRA}58,000</div></div></div>
                    <div className="pc"><div className="pill">Beauty</div><div className="im"></div><div className="in"><div className="nm">Lip Gloss</div><div className="pr">{NAIRA}12,000</div></div></div>
                    <div className="pc"><div className="pill">Watches</div><div className="im"></div><div className="in"><div className="nm">Classic Watch</div><div className="pr">{NAIRA}96,000</div></div></div>
                  </div>
                </div></div>
              <div className="cap">Your real storefront &mdash; your logo, hero, categories and products, on your own domain.</div>
            </div>
            {/* 2 pos */}
            <div className={'screen' + (active === 2 ? ' on' : '')}>
              <div className="frame"><div className="bar"><i></i><i></i><i></i><span className="url">app.palstro.com/pos</span></div>
                <div className="pos">
                  <div className="prods">
                    <div className="t">Rice 5kg<br />{NAIRA}8,500</div><div className="t">Milk 400g<br />{NAIRA}4,300</div><div className="t">Bread<br />{NAIRA}1,200</div>
                    <div className="t">Sugar 1kg<br />{NAIRA}2,100</div><div className="t">Oil 1L<br />{NAIRA}3,900</div><div className="t">Soap<br />{NAIRA}900</div>
                  </div>
                  <div className="cart">
                    <div className="li"><span>Rice 5kg &times;1</span><span>{NAIRA}8,500</span></div>
                    <div className="li"><span>Milk 400g &times;2</span><span>{NAIRA}8,600</span></div>
                    <div className="li"><span>Bread &times;1</span><span>{NAIRA}1,200</span></div>
                    <div className="tot"><span>Total</span><span>{NAIRA}18,300</span></div>
                    <div className="pay">Take payment</div>
                  </div>
                </div></div>
              <div className="cap">Fast POS at the counter &mdash; cash, card or transfer, stock updates instantly.</div>
            </div>
            {/* 3 orders */}
            <div className={'screen' + (active === 3 ? ' on' : '')}>
              <div className="frame"><div className="bar"><i></i><i></i><i></i><span className="url">app.palstro.com/orders</span></div>
                <div className="orders">
                  <div className="o"><span className="id">#ON-1042</span><span>Adaeze O. &middot; Lagos</span><span className="s paid">Paid</span><span>{NAIRA}42,000</span></div>
                  <div className="o"><span className="id">#ON-1041</span><span>Bola A. &middot; Ibadan</span><span className="s pod">On delivery</span><span>{NAIRA}12,500</span></div>
                  <div className="o"><span className="id">#ON-1040</span><span>Chidi N. &middot; Abuja</span><span className="s new">New</span><span>{NAIRA}96,000</span></div>
                  <div className="o"><span className="id">#ON-1039</span><span>Ngozi E. &middot; Enugu</span><span className="s paid">Paid</span><span>{NAIRA}8,900</span></div>
                  <div className="o"><span className="id">#ON-1038</span><span>Musa I. &middot; Kano</span><span className="s paid">Paid</span><span>{NAIRA}31,200</span></div>
                </div></div>
              <div className="cap">Every online order in one place &mdash; paid, new, or out for delivery.</div>
            </div>
          </div>
        </div>
      </div></section>

      {/* FEATURES */}
      <section id="features"><div className="wrap">
        <div className="center reveal"><div className="eyebrow">Everything in one place</div><h2 className="mt">One system. No juggling apps.</h2></div>
        <div className="feat reveal">
          <div className="f"><div className="ic">&#128722;</div><b>Sell at your counter</b><p>Fast POS for in-store sales.</p></div>
          <div className="f"><div className="ic">&#127760;</div><b>Sell on your website</b><p>Your own online store.</p></div>
          <div className="f"><div className="ic">&#128230;</div><b>Same stock, everywhere</b><p>In-store and online, one count.</p></div>
          <div className="f"><div className="ic">&#128179;</div><b>Get paid</b><p>Card, transfer, or on delivery.</p></div>
          <div className="f"><div className="ic">&#128172;</div><b>Orders on WhatsApp</b><p>Confirmations customers love.</p></div>
          <div className="f"><div className="ic">&#128202;</div><b>Books &amp; payroll</b><p>Accounts and staff, sorted.</p></div>
          <div className="f"><div className="ic">&#127912;</div><b>Your own domain &amp; look</b><p>Pick a theme, add your brand.</p></div>
          <div className="f"><div className="ic">&#128276;</div><b>Know what needs you</b><p>Low stock, unpaid bills, flagged.</p></div>
        </div>
      </div></section>

      {/* SHOPS */}
      <section style={{ background: 'var(--soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}><div className="wrap">
        <div className="center reveal"><div className="eyebrow">Made for your kind of shop</div><h2 className="mt">Whatever you sell, it fits.</h2></div>
        <div className="shops reveal">
          <div className="shop"><div className="top">The boutique</div><div className="b"><b>Fashion &amp; beauty</b><p>A beautiful storefront, your brand, your own domain.</p></div></div>
          <div className="shop"><div className="top">The provisions store</div><div className="b"><b>Supermarket &amp; groceries</b><p>Fast POS, hundreds of SKUs, tight stock control.</p></div></div>
          <div className="shop"><div className="top">The pharmacy</div><div className="b"><b>Pharmacy &amp; health</b><p>Inventory you can trust, reports that add up.</p></div></div>
        </div>
      </div></section>

      {/* THEMES — live from the storefront theme registry */}
      <section id="themes"><div className="wrap">
        <div className="themes reveal">
          <div className="eyebrow" style={{ color: '#ffb37a' }}>Your shop, your look</div>
          <h2 className="mt">One shop. {THEME_LIST.length} storefront designs.</h2>
          <p className="lead mt">Pick a design, drop in your logo and colour, go live. No developer.</p>
          <div className="tgrid">
            {THEME_LIST.map((t) => (<span key={t.id}>{t.name}</span>))}
          </div>
        </div>
      </div></section>

      {/* STEPS */}
      <section style={{ paddingTop: '20px' }}><div className="wrap">
        <div className="center reveal"><div className="eyebrow">Live in minutes</div><h2 className="mt">Three steps to selling.</h2></div>
        <div className="steps reveal">
          <div className="step"><div className="n">1</div><b>Set up your shop</b><p>Add products, pick a look, set your address.</p></div>
          <div className="step"><div className="n">2</div><b>Sell in-store and online</b><p>Same stock, one place.</p></div>
          <div className="step"><div className="n">3</div><b>Get paid and grow</b><p>Card, transfer or pay-on-delivery.</p></div>
        </div>
      </div></section>

      {/* NIGERIA */}
      <section className="ng"><div className="wrap ngrow reveal">
        <b>Built for Nigeria:</b><span>{NAIRA} Naira</span><span>Paystack</span><span>WhatsApp orders</span><span>Pay on delivery</span><span>Your own domain</span>
      </div></section>

      {/* PIZZA — the easy price framing (entry price is live from the tiers) */}
      <section><div className="wrap">
        <div className="pizza reveal">
          <div>
            <div className="eyebrow">The easiest maths you&apos;ll do today</div>
            <h2 className="mt">A pizza is gone by tonight. Palstro pays you back all month.</h2>
            <p className="lead">For about the price of one pizza, you get the whole system &mdash; no per-feature charges, no surprises. If it saves you one case of spoiled stock, it has already paid for itself.</p>
            <ul className="plist">
              <li><span className="ck">&#10003;</span> Inventory, sales, and your online store in one place</li>
              <li><span className="ck">&#10003;</span> Real profit and margin on every product</li>
              <li><span className="ck">&#10003;</span> Payments by card, transfer, and on delivery</li>
              <li><span className="ck">&#10003;</span> Staff, payroll, and reports built in</li>
            </ul>
          </div>
          <div>
            <div className="compare">
              <div className="cc">
                <div className="emoji">&#127829;</div>
                <div className="lab">One pizza</div>
                <div className="val">{fmtNaira(tiers[0]?.price ?? 7500)}</div>
                <div className="lab">Gone tonight</div>
              </div>
              <div className="cc">
                <div className="emoji">&#128200;</div>
                <div className="lab">{tiers[0]?.name ?? 'Starter'}</div>
                <div className="val brand">{fmtNaira(tiers[0]?.price ?? 7500)}<small>/mo</small></div>
                <div className="lab">Works all month</div>
              </div>
            </div>
            <a className="btn" onClick={goRegister}>Start free for 14 days</a>
          </div>
        </div>
      </div></section>

      {/* PRICING */}
      <section id="pricing"><div className="wrap">
        <div className="center reveal"><div className="eyebrow">Simple pricing</div><h2 className="mt">Run your whole shop for less than a plate of jollof a day.</h2></div>
        <div className="price reveal">
          {tiers.map((tier, i) => {
            const anchor = TIER_ANCHORS[i] ?? ''
            const pop = i === 1
            return (
              <div key={tier.id} className={'plan' + (pop ? ' pop' : '')}>
                {pop && <div className="tag">Most popular</div>}
                <h3>{tier.name}</h3>
                <div className="amt">{fmtNaira(tier.price)}<small>/mo</small></div>
                <div className="anchor">{anchor}</div>
                <ul>{tier.inclusions.map((f, k) => (<li key={k}>{f}</li>))}</ul>
                <div className="see">See everything included &darr;</div>
                <a className="btn" onClick={goRegister}>Start free</a>
              </div>
            )
          })}
          {/* Online Store add-on — fixed copy, live price from platform_settings */}
          <div className="plan addon">
            <h3>+ Online Store</h3>
            <div className="amt">{fmtNaira(onlineStore.price)}<small>{periodSuffix(onlineStore.months)}</small></div>
            <div className="anchor">less than a pizza a month</div>
            <ul>
              {(onlineStore.inclusions?.length
                ? onlineStore.inclusions
                : [
                    'Your own website',
                    `${THEME_LIST.length} designs + your domain`,
                    'Card & pay-on-delivery',
                    'WhatsApp confirmations',
                  ]
              ).map((f, k) => (<li key={k}>{f}</li>))}
            </ul>
            <div className="see">Add on to any plan</div>
            <a className="btn" onClick={goRegister}>Start free</a>
          </div>
        </div>
        <div className="pricenote">Prices and what each plan unlocks are set by Palstro and shown here automatically &mdash; always up to date.</div>
      </div></section>

      {/* TESTIMONIALS — from the admin Customer Showcase (published only) */}
      <section style={{ background: 'var(--soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}><div className="wrap">
        <div className="center reveal"><div className="eyebrow">Real shops, real results</div><h2 className="mt">Don&apos;t take our word for it.</h2></div>
        <div className="tst reveal">
          {showcase.length > 0
            ? showcase.map((s) => <ShowcaseCard key={s.id} entry={s} />)
            : PLACEHOLDER_TESTIMONIALS.map((q, i) => (
              <div className="card" key={i}>
                <div className="pic"><span>Owner photo</span></div>
                <div className="det"><div className="q">&ldquo;{q}&rdquo;</div>
                  <div className="who"><b>[Owner name]</b><span>[Shop name] &middot; [Location]</span>
                    <div className="links">&#127760; website &middot; &#128247; Instagram &middot; &#128172; WhatsApp</div></div></div>
              </div>
            ))}
        </div>
        {showcase.length === 0 && (
          <div className="placeholder">Testimonials, photos, shop names, locations, socials and websites come from your admin showcase &mdash; each a real, contactable customer.</div>
        )}
      </div></section>

      {/* FAQ */}
      <section><div className="wrap">
        <div className="center reveal"><div className="eyebrow">Questions</div><h2 className="mt">Good to know.</h2></div>
        <div className="faq reveal">
          <div className="qa"><b>Do I need a developer?</b><p>No. Add products, pick a design, add your logo and colour, and you&apos;re live.</p></div>
          <div className="qa"><b>Can I use my own domain?</b><p>Yes. Bring your own (like yourshop.com) and we handle HTTPS automatically.</p></div>
          <div className="qa"><b>How do customers pay?</b><p>Card and transfer via Paystack, or pay-on-delivery. Money lands in your account.</p></div>
          <div className="qa"><b>Is there a free trial?</b><p>Yes, start free. Add the online store whenever you&apos;re ready to sell online.</p></div>
        </div>
      </div></section>

      <section style={{ paddingTop: '10px' }}><div className="wrap">
        <div className="final reveal"><h2>Ready to run your business and sell everywhere?</h2><p className="mt" style={{ opacity: 0.92 }}>Free to start. Live in minutes.</p>
          <div className="finalcta">
            <a className="btn" onClick={goRegister}>Start free</a>
            <a className="btn wa" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">&#128172; Chat with us on WhatsApp</a>
          </div>
        </div>
      </div></section>

      <footer><div className="wrap fcols">
        <div className="logo"><span className="p">P</span> Palstro</div>
        <div>Product &middot; Pricing &middot; Storefronts &middot; Help</div>
        <div>
          <a className="wa-link" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">&#128172; Reach us fast on WhatsApp</a>
          <br />{WHATSAPP_DISPLAY}
        </div>
        <div>&copy; 2026 Palstro &middot; Built for Nigerian businesses</div>
      </div></footer>

      {/* Floating WhatsApp button (sticky, bottom-right) */}
      <a className="wa-float" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp">&#128172;</a>
    </div>
  )
}

// ── Testimonial card (real showcase entry) ──────────────────────────────────
function ShowcaseCard({ entry }: { entry: PublicShowcaseEntry }) {
  const links: Array<{ href: string; label: string }> = []
  if (entry.website_url) links.push({ href: entry.website_url, label: '🌐 website' })
  if (entry.instagram_url) links.push({ href: entry.instagram_url, label: '📷 Instagram' })
  if (entry.facebook_url) links.push({ href: entry.facebook_url, label: '👍 Facebook' })
  if (entry.tiktok_url) links.push({ href: entry.tiktok_url, label: '♪ TikTok' })
  if (entry.whatsapp_url) links.push({ href: entry.whatsapp_url, label: '💬 WhatsApp' })

  const photo = entry.owner_photo_url
  return (
    <div className="card">
      <div className="pic" style={photo ? { backgroundImage: `url(${photo})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
        {!photo && <span>Owner photo</span>}
      </div>
      <div className="det">
        <div className="q">&ldquo;{entry.quote}&rdquo;</div>
        <div className="who">
          <b>{entry.company_name}</b>
          <span>{entry.location || ''}</span>
          {links.length > 0 && (
            <div className="links">
              {links.map((l, i) => (
                <a key={i} href={l.href} target="_blank" rel="noopener noreferrer nofollow">{l.label}</a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Pricing helpers ─────────────────────────────────────────────────────────
const TIER_ANCHORS = ['the essentials', 'for a growing shop', 'the full engine']

// Fallback tier data mirrors the design when subscription_plans hasn't loaded / is empty.
const FALLBACK_TIERS = [
  { name: 'Starter', price: 7500 },
  { name: 'Growth', price: 15500 },
  { name: 'Pro', price: 25000 },
]

const DESIGN_FEATURES: string[][] = [
  ['POS & sales', '1 location', 'Products & stock', 'Basic reports'],
  ['Everything in Starter', 'Up to 3 locations', 'Purchases & expenses', 'Contacts & incentives'],
  ['Everything in Growth', 'Unlimited locations', 'Accounting & payroll', 'Full reports'],
]

// Prefer the plan's own feature list (from subscription_plans, and — when the module-level
// Plan Config lands — the same field can carry the auto-generated inclusions). Falls back to
// the design's short list per tier index so the section always reads well.
function deriveFeatures(plan: unknown, i: number): string[] {
  const feats = (plan as { features?: unknown })?.features
  if (Array.isArray(feats) && feats.length) {
    return (feats as unknown[]).map(String).slice(0, 4)
  }
  return DESIGN_FEATURES[i] ?? []
}

const PLACEHOLDER_TESTIMONIALS = [
  'Our stock and our online orders are finally the same thing. No more spreadsheets.',
  'Set up my online store in an afternoon, picked a theme, added my domain. Customers pay on WhatsApp now.',
  'POS is fast and the reports finally add up. My accountant is happy for once.',
]

// ── Scoped design CSS (ported from palstro_marketing_home_v2.html, prefixed .mkt) ──
const MKT_CSS = `
.mkt{--brand:#F26F21;--brand-d:#d65c12;--ink:#141821;--mut:#5f6672;--line:#eceae7;--bg:#ffffff;--soft:#fbf7f3;--dark:#17150f;--green:#12a150;--teal:#0ea5a4;--slate:#9aa3af;
  font-family:'Maven Pro','Segoe UI',system-ui,-apple-system,Roboto,Arial,sans-serif;color:var(--ink);background:var(--bg);line-height:1.5;-webkit-font-smoothing:antialiased;min-height:100vh}
.mkt *{box-sizing:border-box;margin:0;padding:0}
.mkt .wrap{max-width:1120px;margin:0 auto;padding:0 22px}
.mkt a{color:inherit;text-decoration:none;cursor:pointer}
.mkt .btn{display:inline-block;background:var(--brand);color:#fff;font-weight:700;font-size:15px;padding:13px 24px;border-radius:11px;cursor:pointer;transition:.15s;border:none}
.mkt .btn:hover{background:var(--brand-d);transform:translateY(-1px)}
.mkt .btn.ghost{background:transparent;color:var(--ink);border:1.5px solid var(--line)}
.mkt .btn.ghost:hover{background:var(--soft)}
.mkt .eyebrow{font-size:12px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:var(--brand)}
.mkt h1{font-size:50px;font-weight:900;letter-spacing:-.03em;line-height:1.02}
.mkt h2{font-size:33px;font-weight:900;letter-spacing:-.02em}
.mkt .lead{font-size:18px;color:var(--mut)}
.mkt section{padding:62px 0}
.mkt .center{text-align:center}.mkt .mt{margin-top:12px}
.mkt .reveal{opacity:0;transform:translateY(18px);transition:opacity .6s ease,transform .6s ease}
.mkt .reveal.in{opacity:1;transform:none}
.mkt nav{position:sticky;top:0;background:rgba(255,255,255,.9);backdrop-filter:blur(8px);border-bottom:1px solid var(--line);z-index:30}
.mkt nav .row{display:flex;align-items:center;justify-content:space-between;height:64px}
.mkt .logo{display:flex;align-items:center;gap:9px;font-weight:900;font-size:19px;letter-spacing:-.02em}
.mkt .logo .p{width:30px;height:30px;background:var(--brand);color:#fff;border-radius:8px;display:flex;align-items:center;justify-content:center;font-weight:900}
.mkt .navlinks{display:flex;gap:26px;font-size:14.5px;font-weight:600;color:#333}
.mkt .navlinks a:hover{color:var(--brand)}
.mkt .navcta{display:flex;gap:12px;align-items:center}.mkt .navcta .login{font-weight:700;font-size:14.5px}
.mkt .hero{padding:66px 0 30px;background:radial-gradient(120% 80% at 78% 0%,#fff2e8 0%,#fff 55%)}
.mkt .herogrid{display:grid;grid-template-columns:1fr 1fr;gap:40px;align-items:center}
.mkt .hero .lead{max-width:520px;margin-top:16px}
.mkt .hero .cta{margin-top:24px;display:flex;gap:12px;flex-wrap:wrap}
.mkt .hero .fine{margin-top:11px;font-size:13px;color:var(--mut)}
.mkt .frame{border-radius:14px;border:1px solid var(--line);box-shadow:0 24px 50px -24px rgba(0,0,0,.28);overflow:hidden;background:#fff}
.mkt .frame .bar{height:32px;background:#f4f2ef;display:flex;align-items:center;gap:6px;padding:0 12px;border-bottom:1px solid var(--line)}
.mkt .frame .bar i{width:9px;height:9px;border-radius:99px;background:#d8d5d1;display:inline-block}
.mkt .frame .url{margin-left:10px;font-size:10px;color:#9a948c;background:#fff;border:1px solid var(--line);border-radius:6px;padding:3px 10px}
.mkt .herovis{position:relative}
.mkt .phone{position:absolute;right:-14px;bottom:-22px;width:132px;border-radius:16px;border:5px solid #14120e;overflow:hidden;box-shadow:0 18px 34px -14px rgba(0,0,0,.4);background:#fff}
.mkt .dash{padding:14px;background:#fbfaf8}
.mkt .dhead{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px}
.mkt .dhead b{font-size:13px}.mkt .dhead .pill{font-size:9px;background:#eafaf0;color:var(--green);font-weight:700;padding:3px 8px;border-radius:99px}
.mkt .kpis{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:10px}
.mkt .kpi{background:#fff;border:1px solid var(--line);border-radius:9px;padding:9px}
.mkt .kpi b{font-size:15px}.mkt .kpi span{font-size:8.5px;color:var(--mut);text-transform:uppercase;letter-spacing:.06em;display:block;margin-top:1px}
.mkt .kpi em{font-style:normal;font-size:8.5px;color:var(--green);font-weight:700}
.mkt .drow{display:grid;grid-template-columns:1.5fr 1fr;gap:8px}
.mkt .panel{background:#fff;border:1px solid var(--line);border-radius:9px;padding:10px}
.mkt .panel .t{font-size:9px;color:var(--mut);text-transform:uppercase;letter-spacing:.07em;margin-bottom:8px;font-weight:700}
.mkt .bars{display:flex;align-items:flex-end;gap:7px;height:78px;position:relative}
.mkt .bars .g{position:absolute;left:0;right:0;height:1px;background:#f0eeeb}
.mkt .bars .col{flex:1;display:flex;flex-direction:column;justify-content:flex-end;height:100%;position:relative;z-index:1}
.mkt .bars .col b{display:block;background:var(--brand);border-radius:4px 4px 0 0}
.mkt .bars .lbl{display:flex;gap:7px;margin-top:5px}
.mkt .bars .lbl span{flex:1;text-align:center;font-size:7.5px;color:var(--mut)}
.mkt .lbl{display:flex;gap:7px;margin-top:5px}
.mkt .lbl span{flex:1;text-align:center;font-size:7.5px;color:var(--mut)}
.mkt .donut{width:78px;height:78px;border-radius:99px;margin:2px auto 8px;background:conic-gradient(var(--brand) 0 55%, var(--teal) 55% 85%, var(--slate) 85% 100%);-webkit-mask:radial-gradient(circle 21px at center,transparent 98%,#000 100%);mask:radial-gradient(circle 21px at center,transparent 98%,#000 100%)}
.mkt .leg{font-size:8.5px;color:var(--mut)}
.mkt .leg div{display:flex;align-items:center;gap:5px;margin-bottom:3px}
.mkt .leg i{width:8px;height:8px;border-radius:2px;display:inline-block}
.mkt .showcase{background:var(--dark);color:#fff;border-radius:22px;padding:36px}
.mkt .showcase h2{color:#fff}.mkt .showcase .lead{color:#c9c6c1}
.mkt .tabs{display:flex;gap:8px;flex-wrap:wrap;margin:20px 0 18px;justify-content:center}
.mkt .tabs button{background:rgba(255,255,255,.08);color:#fff;border:1px solid rgba(255,255,255,.14);font-weight:700;font-size:13px;padding:9px 16px;border-radius:99px;cursor:pointer;transition:.15s}
.mkt .tabs button.on{background:var(--brand);border-color:var(--brand)}
.mkt .screens{position:relative;max-width:860px;margin:0 auto}
.mkt .screen{display:none}
.mkt .screen.on{display:block;animation:mktfade .5s ease}
@keyframes mktfade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
.mkt .screen .cap{color:#c9c6c1;font-size:13px;text-align:center;margin-top:14px}
.mkt .store{background:#fff;color:var(--ink)}
.mkt .store .mq{background:#111;color:#fff;font-size:9px;letter-spacing:.16em;text-transform:uppercase;text-align:center;padding:6px;white-space:nowrap;overflow:hidden}
.mkt .store .sh{display:flex;align-items:center;gap:10px;padding:11px 16px;border-bottom:1px solid var(--line)}
.mkt .store .sh .lg{font-weight:900;font-size:13px;white-space:nowrap}
.mkt .store .sh .sr{flex:1;border:1px solid var(--line);border-radius:99px;font-size:10px;color:#9a948c;padding:8px 14px}
.mkt .store .sh .ac{font-size:10px;font-weight:700;white-space:nowrap}
.mkt .store .sh .cr{background:var(--green);color:#fff;font-size:10px;font-weight:800;padding:7px 12px;border-radius:8px;white-space:nowrap}
.mkt .store .hero2{position:relative;height:118px;background:linear-gradient(120deg,#c98f6a,#7a4a2e);display:flex;flex-direction:column;justify-content:center;padding:16px;color:#fff}
.mkt .store .hero2 b{font-size:20px;font-weight:900}.mkt .store .hero2 small{font-size:10px;opacity:.9}
.mkt .store .hero2 .dots{position:absolute;bottom:9px;left:16px;display:flex;gap:5px}
.mkt .store .hero2 .dots i{width:14px;height:3px;border-radius:9px;background:rgba(255,255,255,.5)}.mkt .store .hero2 .dots i.on{background:#fff}
.mkt .store .na{font-size:14px;font-weight:900;letter-spacing:.02em;padding:13px 16px 6px}
.mkt .store .pg{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:10px;padding:0 16px 16px}
.mkt .store .pc{border:1px solid var(--line);border-radius:10px;overflow:hidden;position:relative}
.mkt .store .pc .pill{position:absolute;top:7px;left:7px;background:#fff;font-size:8px;font-weight:700;padding:3px 8px;border-radius:99px;box-shadow:0 1px 4px rgba(0,0,0,.12);z-index:1}
.mkt .store .pc .im{aspect-ratio:1/1;background:#f4f2ef}
.mkt .store .pc .in{padding:7px}.mkt .store .pc .nm{font-size:9px;font-weight:700}.mkt .store .pc .pr{font-size:9px;color:var(--mut)}
.mkt .pos{display:grid;grid-template-columns:1.4fr 1fr;gap:12px;background:#fff;color:var(--ink);padding:14px}
.mkt .pos .prods{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px}
.mkt .pos .prods .t{aspect-ratio:1.2/1;border:1px solid var(--line);border-radius:8px;display:flex;flex-direction:column;justify-content:flex-end;padding:7px;font-size:9px;font-weight:700}
.mkt .pos .cart{border:1px solid var(--line);border-radius:10px;padding:11px;display:flex;flex-direction:column}
.mkt .pos .li{display:flex;justify-content:space-between;font-size:10px;padding:5px 0;border-bottom:1px solid #f2f0ed}
.mkt .pos .tot{margin-top:auto;display:flex;justify-content:space-between;font-weight:900;font-size:14px;padding-top:8px}
.mkt .pos .pay{margin-top:9px;background:var(--green);color:#fff;text-align:center;font-weight:800;font-size:11px;padding:9px;border-radius:8px}
.mkt .orders{background:#fff;color:var(--ink);padding:6px 14px 14px}
.mkt .orders .o{display:grid;grid-template-columns:1.4fr 1fr .8fr .8fr;gap:8px;align-items:center;padding:10px 0;border-bottom:1px solid #f2f0ed;font-size:11px}
.mkt .orders .o .id{font-weight:800}
.mkt .orders .s{font-size:9px;font-weight:800;padding:3px 8px;border-radius:99px;justify-self:start}
.mkt .s.paid{background:#eafaf0;color:var(--green)}.mkt .s.new{background:#fff2e9;color:var(--brand)}.mkt .s.pod{background:#eef2ff;color:#4f5bd5}
.mkt .feat{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:16px;margin-top:34px}
.mkt .feat .f{border:1px solid var(--line);border-radius:14px;padding:18px;transition:.15s}
.mkt .feat .f:hover{transform:translateY(-3px);box-shadow:0 14px 30px -18px rgba(0,0,0,.2)}
.mkt .feat .f .ic{width:38px;height:38px;border-radius:10px;background:#fff2e9;color:var(--brand);display:flex;align-items:center;justify-content:center;font-size:19px;margin-bottom:12px}
.mkt .feat .f b{font-size:15px}.mkt .feat .f p{font-size:13px;color:var(--mut);margin-top:3px}
.mkt .shops{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;margin-top:32px}
.mkt .shop{border-radius:16px;overflow:hidden;border:1px solid var(--line);transition:.15s}
.mkt .shop:hover{transform:translateY(-3px);box-shadow:0 16px 34px -20px rgba(0,0,0,.25)}
.mkt .shop .top{height:120px;display:flex;align-items:flex-end;padding:14px;color:#fff;font-weight:800}
.mkt .shop:nth-child(1) .top{background:linear-gradient(135deg,#7a4a2e,#c98f6a)}
.mkt .shop:nth-child(2) .top{background:linear-gradient(135deg,#149a52,#7bd39a)}
.mkt .shop:nth-child(3) .top{background:linear-gradient(135deg,#2563eb,#7aa7ff)}
.mkt .shop .b{padding:14px}.mkt .shop .b p{font-size:13.5px;color:var(--mut);margin-top:4px}
.mkt .themes{background:var(--dark);color:#fff;border-radius:20px;padding:40px}
.mkt .themes h2{color:#fff}.mkt .themes .lead{color:#c9c6c1}
.mkt .tgrid{display:flex;flex-wrap:wrap;gap:10px;margin-top:22px}
.mkt .tgrid span{border:1px solid rgba(255,255,255,.18);border-radius:99px;padding:9px 16px;font-size:13px;font-weight:700}
.mkt .steps{display:grid;grid-template-columns:1fr 1fr 1fr;gap:18px;margin-top:32px}
.mkt .step .n{width:34px;height:34px;border-radius:99px;background:var(--brand);color:#fff;font-weight:900;display:flex;align-items:center;justify-content:center;margin-bottom:12px}
.mkt .step b{font-size:17px}.mkt .step p{font-size:14px;color:var(--mut);margin-top:4px}
.mkt .ng{background:var(--soft);border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.mkt .ngrow{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;align-items:center}
.mkt .ngrow b{font-size:15px}
.mkt .ngrow span{font-size:14px;font-weight:700;color:#444;background:#fff;border:1px solid var(--line);padding:9px 16px;border-radius:10px}
.mkt .price{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:16px;margin-top:34px;align-items:stretch}
.mkt .plan{border:1px solid var(--line);border-radius:16px;padding:22px;display:flex;flex-direction:column;transition:.15s}
.mkt .plan:hover{transform:translateY(-3px);box-shadow:0 16px 34px -20px rgba(0,0,0,.2)}
.mkt .plan.pop{border:2px solid var(--brand);position:relative}
.mkt .plan.pop .tag{position:absolute;top:-11px;left:22px;background:var(--brand);color:#fff;font-size:11px;font-weight:800;padding:4px 10px;border-radius:99px;text-transform:uppercase;letter-spacing:.06em}
.mkt .plan.addon{background:var(--dark);color:#fff;border-color:var(--dark)}
.mkt .plan h3{font-size:16px;font-weight:800}
.mkt .plan .amt{font-size:30px;font-weight:900;margin-top:8px;letter-spacing:-.02em}
.mkt .plan .amt small{font-size:13px;font-weight:600;color:var(--mut)}.mkt .plan.addon .amt small{color:#bdb9b3}
.mkt .plan .anchor{font-size:12px;color:var(--brand);font-weight:700;margin-top:4px}.mkt .plan.addon .anchor{color:#ffcba6}
.mkt .plan ul{list-style:none;margin:16px 0;font-size:13.5px;display:flex;flex-direction:column;gap:8px}
.mkt .plan li{display:flex;gap:8px}.mkt .plan li:before{content:'\\2713';color:var(--green);font-weight:900}.mkt .plan.addon li:before{color:#5fe3a0}
.mkt .plan .see{font-size:12px;color:var(--mut);margin-bottom:14px}.mkt .plan.addon .see{color:#bdb9b3}
.mkt .plan .btn{margin-top:auto;text-align:center}.mkt .plan.addon .btn{background:#fff;color:var(--dark)}
.mkt .pricenote{text-align:center;font-size:12.5px;color:var(--mut);margin-top:18px}
.mkt .tst{max-width:860px;margin:30px auto 0;display:flex;flex-direction:column;gap:16px}
.mkt .card{display:grid;grid-template-columns:170px 1fr;border:1px solid var(--line);border-radius:16px;overflow:hidden;transition:.15s}
.mkt .card:hover{box-shadow:0 16px 34px -22px rgba(0,0,0,.22)}
.mkt .card .pic{background:linear-gradient(135deg,#e7dccf,#b79c82);min-height:180px;position:relative}
.mkt .card .pic span{position:absolute;bottom:8px;left:8px;font-size:9px;color:#fff;background:rgba(0,0,0,.3);padding:3px 8px;border-radius:6px}
.mkt .card .det{padding:22px}
.mkt .card .q{font-size:15.5px;line-height:1.55}
.mkt .card .who{margin-top:14px}
.mkt .card .who b{font-size:14.5px;display:block}
.mkt .card .who span{font-size:12.5px;color:var(--mut)}
.mkt .card .links{margin-top:9px;font-size:12px;color:var(--brand);font-weight:700;display:flex;gap:14px;flex-wrap:wrap}
.mkt .card .links a:hover{text-decoration:underline}
.mkt .placeholder{text-align:center;font-size:12px;color:var(--mut);margin-top:14px;font-style:italic}
.mkt .faq{max-width:760px;margin:28px auto 0}
.mkt .qa{border-bottom:1px solid var(--line);padding:16px 0}
.mkt .qa b{font-size:15.5px}.mkt .qa p{font-size:14px;color:var(--mut);margin-top:6px}
.mkt .final{background:var(--brand);color:#fff;border-radius:20px;padding:48px;text-align:center;margin:20px 0}
.mkt .final h2{color:#fff}.mkt .final .btn{background:#fff;color:var(--brand);margin-top:20px}
.mkt .finalcta{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin-top:6px}
.mkt .btn.wa{background:#25D366;color:#fff;display:inline-flex;align-items:center;gap:8px}
.mkt .btn.wa:hover{background:#1da851}
.mkt footer{border-top:1px solid var(--line);padding:34px 0;color:var(--mut);font-size:13px}
.mkt .fcols{display:flex;justify-content:space-between;flex-wrap:wrap;gap:20px}
.mkt .wa-link{color:#25D366;font-weight:700}
.mkt .wa-link:hover{text-decoration:underline}
.mkt .wa-float{position:fixed;right:18px;bottom:18px;z-index:60;background:#25D366;color:#fff;width:56px;height:56px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:26px;box-shadow:0 6px 20px rgba(0,0,0,.25)}
.mkt .wa-float:hover{background:#1da851;transform:translateY(-2px)}
/* profit merge: hero price chip, profit chart, levers, pizza */
.mkt .hero h1 .hl{color:var(--brand)}
.mkt .pricechip{display:inline-flex;align-items:center;gap:9px;margin-top:20px;padding:9px 14px;border:1px dashed var(--line);border-radius:999px;font-size:14px;color:var(--mut);background:var(--soft)}
.mkt .pricechip b{color:var(--ink)}
.mkt .pricechip .amt{color:var(--brand-d);font-weight:800}
.mkt .profit{background:var(--soft);border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.mkt .profitgrid{display:grid;grid-template-columns:1fr 1fr;gap:36px;align-items:center}
.mkt .sec-head{max-width:44ch}
.mkt .chartcard{background:#fff;border:1px solid var(--line);border-radius:14px;box-shadow:0 1px 3px rgba(0,0,0,.06),0 1px 2px -1px rgba(0,0,0,.06);padding:20px 20px 16px}
.mkt .chartcard .top{display:flex;justify-content:space-between;align-items:baseline;gap:12px}
.mkt .chartcard .top h3{font-size:16px;font-weight:700}
.mkt .chartcard .tag{font-size:10.5px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:var(--mut);border:1px solid var(--line);padding:3px 8px;border-radius:6px}
.mkt .chart-svg{width:100%;height:auto;display:block;margin-top:8px}
.mkt .legend{display:flex;gap:16px;margin-top:10px;font-size:13px;color:var(--mut);flex-wrap:wrap}
.mkt .legend span{display:inline-flex;align-items:center;gap:7px}
.mkt .swatch{width:14px;height:3px;border-radius:2px;display:inline-block}
.mkt .draw{stroke-dasharray:1400;stroke-dashoffset:1400;animation:mktdraw 1.5s ease .3s forwards}
@keyframes mktdraw{to{stroke-dashoffset:0}}
@media(prefers-reduced-motion:reduce){.mkt .draw{animation:none;stroke-dashoffset:0}}
.mkt .levers{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:30px}
.mkt .lever{background:#fff;border:1px solid var(--line);border-radius:14px;padding:24px;transition:.15s}
.mkt .lever:hover{transform:translateY(-3px);box-shadow:0 14px 30px -18px rgba(0,0,0,.2)}
.mkt .lever .ic{width:44px;height:44px;border-radius:12px;background:#fff2e9;display:grid;place-items:center;margin-bottom:16px}
.mkt .lever .ic svg{width:22px;height:22px;stroke:var(--brand);fill:none;stroke-width:1.9}
.mkt .lever h3{font-size:19px;font-weight:800}
.mkt .lever .promise{margin-top:7px;color:var(--brand);font-weight:700;font-size:14.5px}
.mkt .lever .how{margin-top:11px;color:var(--mut);font-size:15px}
.mkt .pizza{background:var(--dark);color:#fff;border-radius:22px;padding:48px;display:grid;grid-template-columns:1fr 1fr;gap:40px;align-items:center;overflow:hidden}
.mkt .pizza .eyebrow{color:#ffcba6}
.mkt .pizza h2{color:#fff}
.mkt .pizza .lead{color:rgba(255,255,255,.82);margin-top:14px}
.mkt .plist{list-style:none;margin:20px 0 0;display:grid;gap:10px}
.mkt .plist li{display:flex;gap:11px;align-items:flex-start;color:rgba(255,255,255,.92);font-size:15.5px}
.mkt .plist .ck{color:var(--brand);font-weight:900;flex:none}
.mkt .compare{display:flex;gap:14px;align-items:stretch}
.mkt .cc{flex:1;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.14);border-radius:14px;padding:20px;text-align:center}
.mkt .cc .emoji{font-size:32px;line-height:1}
.mkt .cc .lab{margin-top:10px;font-size:13px;color:rgba(255,255,255,.66)}
.mkt .cc .val{margin-top:4px;font-size:21px;font-weight:900}
.mkt .cc .val small{font-size:13px;font-weight:600}
.mkt .cc .val.brand{color:#ffb37a}
.mkt .pizza .btn{display:block;width:100%;text-align:center;margin-top:14px;background:#fff;color:var(--dark)}
.mkt .pizza .btn:hover{background:#f0ece6}
@media(max-width:860px){
  .mkt h1{font-size:34px}.mkt h2{font-size:25px}
  .mkt .herogrid{grid-template-columns:1fr}.mkt .phone{width:104px;right:6px}
  .mkt .feat,.mkt .price{grid-template-columns:1fr 1fr}
  .mkt .feat .f,.mkt .plan{min-width:0}
  .mkt .shops,.mkt .steps{grid-template-columns:1fr}
  .mkt .card{grid-template-columns:1fr}.mkt .card .pic{min-height:130px}
  .mkt .navlinks{display:none}
  .mkt .store .pg,.mkt .pos .prods{grid-template-columns:1fr 1fr}
  .mkt .pos{grid-template-columns:1fr}
  .mkt .profitgrid{grid-template-columns:1fr;gap:26px}
  .mkt .levers{grid-template-columns:1fr}
  .mkt .pizza{grid-template-columns:1fr;padding:32px}
}
`
